import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import type { AudioPlaybackState } from "./audio-playback-state";
import {
  buildBriefJourneyDeviceSpeechPlan,
  buildCanonicalDeviceSpeechPlan,
  buildDashboardDeviceSpeechPlan,
  createDeviceSpeechFallback,
  type BriefJourneyDeviceSpeechPlanInput,
  type DashboardDeviceSpeechPlanInput,
  type DeviceSpeechTimeline,
} from "./device-speech-fallback.mjs";

export type DeviceSpeechPlanKind = "brief-journey" | "dashboard" | "standalone";

type DeviceSpeechHooks = {
  getState: () => AudioPlaybackState;
  setState: (state: AudioPlaybackState) => void;
  setTimelineState: (state: AudioPlaybackState) => void;
  resetMediaSession: () => void;
  setMediaSession: (label: string, detail: string, lang: string) => void;
  onEnded: (key: string) => void;
  onStarted: (state: AudioPlaybackState) => void;
  onAttemptSettled?: () => void;
};

type DeviceSpeechContext = {
  kind: DeviceSpeechPlanKind;
  key: string;
  label: string;
  lang: string;
  rate: number;
  hooks: DeviceSpeechHooks;
};

const controller = createDeviceSpeechFallback();
let context: DeviceSpeechContext | null = null;
let briefJourneyContinuousPlaybackAuthorized = false;

function readyState(current: DeviceSpeechContext): AudioPlaybackState {
  const ready = controller.hasPrepared(current.key);
  return {
    phase: "error",
    key: current.key,
    label: current.label,
    detail: publicWebCopyForLang(current.lang)(
      ready ? "audio.deviceSpeechReady" : "audio.error.browserPlayback",
    ),
    code: "device_speech_interrupted",
    quotaKind: "service",
    deviceSpeechReady: ready,
  };
}

function playingState(current: DeviceSpeechContext): AudioPlaybackState {
  const timeline = controller.getTimeline();
  return {
    phase: "playing",
    key: current.key,
    label: current.label,
    detail: publicWebCopyForLang(current.lang)("audio.deviceSpeechPlaying"),
    paused: false,
    buffering: false,
    positionMs: timeline?.positionMs || 0,
    durationMs: timeline?.durationMs || 0,
    buffered01: 1,
    playbackRate: timeline?.playbackRate || current.rate,
    cache: "device-speech",
    canDownload: false,
    deviceSpeech: true,
  };
}

function publishTimeline(current: DeviceSpeechContext, timeline: DeviceSpeechTimeline) {
  if (context !== current) return;
  const state = current.hooks.getState();
  if (state.phase !== "playing" || state.key !== current.key || !state.deviceSpeech) return;
  current.hooks.setTimelineState({
    ...state,
    positionMs: timeline.positionMs,
    durationMs: timeline.durationMs,
    buffered01: 1,
    playbackRate: timeline.playbackRate,
    paused: timeline.paused,
  });
}

function publishReady(current: DeviceSpeechContext): AudioPlaybackState {
  current.hooks.resetMediaSession();
  const next = readyState(current);
  current.hooks.setState(next);
  current.hooks.onAttemptSettled?.();
  return next;
}

function publishPlaying(current: DeviceSpeechContext): AudioPlaybackState {
  const next = playingState(current);
  current.hooks.setMediaSession(current.label, next.detail || "", current.lang);
  current.hooks.setState(next);
  current.hooks.onStarted(next);
  current.hooks.onAttemptSettled?.();
  return next;
}

function beginPreparedPlayback(current: DeviceSpeechContext): boolean {
  if (context !== current || !controller.hasPrepared(current.key)) return false;
  current.hooks.setState({
    phase: "loading",
    key: current.key,
    label: current.label,
    detail: publicWebCopyForLang(current.lang)("audio.deviceSpeechPlaying"),
    playbackRate: current.rate,
    cache: "device-speech",
    canDownload: false,
    deviceSpeech: true,
  });
  if (!controller.playPrepared()) {
    publishReady(current);
    return false;
  }
  return true;
}

function playCurrentPrepared(current: DeviceSpeechContext): boolean {
  const state = current.hooks.getState();
  return state.phase === "error" && state.deviceSpeechReady === true
    ? beginPreparedPlayback(current)
    : false;
}

export const dashboardDeviceSpeech = Object.freeze({
  prepare(input: {
    body: Record<string, unknown>;
    kind?: DeviceSpeechPlanKind;
    key: string;
    label: string;
    detail?: string;
    lang: string;
    rate: number;
    speechText?: unknown;
    hooks: DeviceSpeechHooks;
  }): boolean {
    const current: DeviceSpeechContext = {
      kind: input.kind || "dashboard",
      key: input.key,
      label: input.label,
      lang: input.lang,
      rate: input.rate,
      hooks: input.hooks,
    };
    const planInput = {
      ...input.body,
      key: input.key,
      label: input.label,
      detail: input.detail,
      lang: input.lang,
      ...(input.speechText === undefined ? {} : { speechText: input.speechText }),
    };
    const plan =
      input.kind === "brief-journey"
        ? buildBriefJourneyDeviceSpeechPlan(planInput as BriefJourneyDeviceSpeechPlanInput)
        : input.kind === "standalone"
          ? buildCanonicalDeviceSpeechPlan(planInput as BriefJourneyDeviceSpeechPlanInput)
          : buildDashboardDeviceSpeechPlan(planInput as DashboardDeviceSpeechPlanInput);
    context = null;
    if (!plan) return false;
    const prepared = controller.prepare(plan, {
      rate: input.rate,
      onStart: () => {
        if (context === current) publishPlaying(current);
      },
      onEnd: () => {
        const state = current.hooks.getState();
        if (state.key !== current.key || !state.deviceSpeech) return;
        current.hooks.resetMediaSession();
        current.hooks.setState({ phase: "idle" });
        current.hooks.onEnded(current.key);
        if (context === current) context = null;
      },
      onError: () => {
        if (current.hooks.getState().key === current.key) publishReady(current);
      },
      onTimeline: (timeline) => publishTimeline(current, timeline),
    });
    if (prepared || controller.hasPrepared(input.key)) context = current;
    return prepared;
  },

  markAuthorized(): AudioPlaybackState | null {
    const current = context;
    if (!current || !controller.hasPrepared(current.key)) return null;
    if (
      current.kind === "brief-journey" &&
      briefJourneyContinuousPlaybackAuthorized &&
      beginPreparedPlayback(current)
    ) {
      return current.hooks.getState();
    }
    return publishReady(current);
  },

  startDashboardPreparedAfterAuthorization(): AudioPlaybackState | null {
    const current = context;
    if (!current || current.kind !== "dashboard" || !controller.hasPrepared(current.key)) {
      return null;
    }
    const started = beginPreparedPlayback(current);
    if (!started && !controller.hasPrepared(current.key)) {
      if (context === current) context = null;
      return null;
    }
    return current.hooks.getState();
  },

  startBriefJourneyPreparedFromUserGesture(): AudioPlaybackState | null {
    const current = context;
    if (!current || current.kind !== "brief-journey") return null;
    if (!beginPreparedPlayback(current)) return current.hooks.getState();
    briefJourneyContinuousPlaybackAuthorized = true;
    return current.hooks.getState();
  },

  playPrepared(): boolean {
    const current = context;
    if (!current || !playCurrentPrepared(current)) return false;
    if (current.kind === "brief-journey") briefJourneyContinuousPlaybackAuthorized = true;
    return true;
  },

  pause(): boolean {
    return controller.pause();
  },

  resume(): boolean {
    return controller.resume();
  },

  setRate(value: number): number {
    const rate = controller.setRate(value);
    if (context) context.rate = rate;
    return rate;
  },

  seekToPositionMs(value: number): boolean {
    return controller.seekToPositionMs(value);
  },

  getTimeline(): DeviceSpeechTimeline | null {
    return controller.getTimeline();
  },

  discard(): boolean {
    context = null;
    return controller.discard();
  },

  resetBriefJourneyAuthorization() {
    briefJourneyContinuousPlaybackAuthorized = false;
  },
});
